import config from '../config';
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { isUUID } from "validator";
import { useAuth } from "../context/authContext";
import Button from "../components/Button";
import OrganizationLocation from "../components/OrganizationLocation";
import "../style/createOrganization.css";
import axios from "axios";
import { UserCircle, Trash2 } from "lucide-react";

const CreateOrganization = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = useParams();
  const { editMode } = location.state || {};
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const formRef = React.useRef();
  const logoInputRef = React.useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // Store full S3 object key (folder/file), not just original filename.
  const [organizationLogoFileName, setOrganizationLogoFileName] = useState("");
  const [initialOrganizationLogoFileName, setInitialOrganizationLogoFileName] =
    useState("");
  const [organizationLogoUrl, setOrganizationLogoUrl] = useState("");
  const todayDate = new Date().toISOString().split("T")[0];

  // flag for Inventory Access
  const isInventoryAccessEnabled = config.INVENTORY_ACCESS_ENABLED;

  const [title, setTitle] = useState("Create Organization");
  const [activeTab, setActiveTab] = useState("inventory");
  

  const [lockedFields, setLockedFields] = useState({
    couchbaseScope: false,
    syncGatewayDB: false,
    dbUserRole: false,
  });

  const [fieldsUpdated, setFieldsUpdated] = useState({
    couchbaseScope: false,
    syncGatewayDB: false,
    dbUserRole: false,
  });

  useEffect(() => {
    if (editMode) {
      setTitle("Edit Organization");
    }
  }, [editMode]);

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    city: "",
    state: "",
    country: "",
    type: "",
    joinedDate: todayDate,
    notes: "",
    isActive: false,
    vastOnly: false,
    couchbaseScope: "",
    syncGatewayDB: "",
    dbUserRole: "",

    usingInventoryManagement: false,
    locationHierarchy: [],
    inventoryCategories: {
      medicationCategories: "",
      equipmentCategories: "",
      suppliesCategories: "",
      foodCategories: "",
      medicationCategoriesRestriction: false,
      equipmentCategoriesRestriction: false,
      suppliesCategoriesRestriction: false,
      foodCategoriesRestriction: false,
    },
  });

  

  const hasFormChanges = () => {
    if (!editMode || !initialFormData) return true;
    const formChanged =
      JSON.stringify(formData) !== JSON.stringify(initialFormData);
    const logoChanged =
      (organizationLogoFileName || "") !== (initialOrganizationLogoFileName || "");
    return formChanged || logoChanged;
  };

  const isFormValid = () => {
    if (editMode) return hasFormChanges();
    return (
      formData.name.trim() !== "" &&
      formData.city.trim() !== "" &&
      formData.state.trim() !== "" &&
      formData.country.trim() !== "" &&
      formData.type.trim() !== "" &&
      formData.joinedDate.trim() !== ""
    );
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;


    const restrictedFields = ['couchbaseScope', 'syncGatewayDB', 'dbUserRole'];
    if (restrictedFields.includes(name) && lockedFields[name]) {
      return; 
    }

    let formattedValue = value;
    if (name === "type") {
      const typeMapping = {
        NGO: "NGO",
        Hospital: "HOSPITAL",
        "Short Term": "SHORT_TERM",
      };
      formattedValue = typeMapping[value] || value;
    }

    const newFormData = {
      ...formData,
      [name]: type === "checkbox" ? checked : formattedValue,
    };

    setFormData(newFormData);


  };

  const handleLocationHierarchyChange = (newHierarchy) => {
    setFormData({
      ...formData,
      locationHierarchy: newHierarchy,
    });
  };

  useEffect(() => {
    if (!organizationLogoFileName) {
      setOrganizationLogoUrl("");
      return;
    }

    const fetchImageUrl = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/get-image-url`, {
          params: {
            // Backend expects the full S3 key (including folder) already.
            fileName: organizationLogoFileName,
          },
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
          },
        });
        setOrganizationLogoUrl(response.data.presignedUrl);
      } catch (error) {
        console.error("Error fetching logo image URL:", error);
      }
    };

    fetchImageUrl();
  }, [organizationLogoFileName, authData.accessToken]);

  const handleOrganizationLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      alert("Please upload a valid image file (png, jpg, jpeg).");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert("File size should not exceed 2MB.");
      return;
    }

    setUploadingLogo(true);

    try {
      const originalName = file.name || "logo";
      const extMatch = originalName.match(/(\.[a-zA-Z0-9]+)$/);
      const ext = extMatch ? extMatch[1].toLowerCase() : "";
      const safeBase = originalName
        .replace(ext, "")
        .trim()
        .replace(/[^a-zA-Z0-9-_]+/g, "_")
        .slice(0, 60) || "logo";

      const orgKeyPart = (formData.id || organizationId || "new")
        .toString()
        .replace(/[^a-zA-Z0-9-_]+/g, "_");
      const rand =
        (typeof window !== "undefined" &&
          window.crypto &&
          typeof window.crypto.randomUUID === "function" &&
          window.crypto.randomUUID()) ||
        `${Date.now()}_${Math.random().toString(16).slice(2)}`;

      // Ensure each org upload gets a unique key so files don't overwrite.
      const uniqueFileName = `org_${orgKeyPart}_${Date.now()}_${rand}_${safeBase}${ext}`;

      const response = await axios.get(`${config.BASE_URL}/get-presigned-url`, {
        params: {
          fileName: uniqueFileName,
          contentType: file.type,
        },
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
        },
      });

      const { presignedUrl, fileName: key } = response.data;

      const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(
          `Failed to upload the file to S3: ${uploadResponse.statusText}. Details: ${errorText}`
        );
      }

      // Persist the exact key returned by backend (folder/fileName)
      setOrganizationLogoFileName(key || file.name);
    } catch (error) {
      console.error("Organization logo upload error:", error);
      alert("Failed to upload the logo. Please try again.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleOrganizationLogoDelete = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (!window.confirm("Are you sure you want to delete the logo?")) return;

    setOrganizationLogoFileName("");
    setOrganizationLogoUrl("");

    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };



  useEffect(() => {
    const fetchOrgData = async () => {
      if (!isUUID(organizationId)) {
        console.error(`Invalid UUID: ${organizationId}`);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${config.BASE_URL}/organizations/${organizationId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch org data");
        }

        const orgData = await response.json();

        // Preload persisted organization logo (S3 key) if available
        setOrganizationLogoFileName(orgData.organizationLogoFileName ?? "");
        setInitialOrganizationLogoFileName(orgData.organizationLogoFileName ?? "");

        setFormData((prevOrg) => {
          let formattedDate = "";
          const dateValue = orgData.joinedDate || orgData.joineddate;

          if (dateValue) {
            try {
              const date = new Date(dateValue);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                formattedDate = `${year}-${month}-${day}`;
              } else {
                console.error("Invalid date:", dateValue);
              }
            } catch (error) {
              console.error("Error parsing date:", error);
            }
          }

          const couchbaseScope = orgData.couchbaseScope ?? "";
          const syncGatewayDB = orgData.syncGatewayDB ?? "";
          const dbUserRole = orgData.dbUserRole ?? "";

          // For existing orgs: lock fields that already have values (they've been saved)
          // Only allow editing if the field is empty (one-time update)
          setLockedFields({
            couchbaseScope: couchbaseScope.trim() !== "", 
            syncGatewayDB: syncGatewayDB.trim() !== "",
            dbUserRole: dbUserRole.trim() !== "",
          });
          
          // Reset fieldsUpdated state when loading existing org
          setFieldsUpdated({
            couchbaseScope: couchbaseScope.trim() !== "",
            syncGatewayDB: syncGatewayDB.trim() !== "",
            dbUserRole: dbUserRole.trim() !== "",
          });

          let parsedInventoryCategories = orgData.inventoryCategories ?? {};
          if (typeof parsedInventoryCategories === "string") {
            try {
              parsedInventoryCategories = JSON.parse(parsedInventoryCategories);
            } catch (e) {
              console.error("Error parsing organization inventoryCategories:", e);
              parsedInventoryCategories = {};
            }
          }

          const loadedFormData = {
            ...prevOrg,
            // explicitly map every field you care about:
            id: orgData.id ?? orgData.organizationId,
            name: orgData.name,
            city: orgData.city,
            state: orgData.state,
            country: orgData.country,
            type: orgData.type,
            joinedDate: formattedDate,
            notes: orgData.notes ?? "",
            isActive: orgData.isActive ?? orgData.active ?? false,
            vastOnly: orgData.vastOnly ?? false,
            couchbaseScope: couchbaseScope,
            syncGatewayDB: syncGatewayDB,
            dbUserRole: dbUserRole,

            usingInventoryManagement: orgData.usingInventoryManagement ?? false,
            locationHierarchy: orgData.locationHierarchy ?? [],
            inventoryCategories: {
              medicationCategories: parsedInventoryCategories.medicationCategories ?? "",
              equipmentCategories: parsedInventoryCategories.equipmentCategories ?? "",
              suppliesCategories: parsedInventoryCategories.suppliesCategories ?? "",
              foodCategories: parsedInventoryCategories.foodCategories ?? "",
              medicationCategoriesRestriction:
                !!parsedInventoryCategories.medicationCategoriesRestriction,
              equipmentCategoriesRestriction:
                !!parsedInventoryCategories.equipmentCategoriesRestriction,
              suppliesCategoriesRestriction:
                !!parsedInventoryCategories.suppliesCategoriesRestriction,
              foodCategoriesRestriction:
                !!parsedInventoryCategories.foodCategoriesRestriction,
            },
          };
          

          setInitialFormData(loadedFormData);
          
          return loadedFormData;
        });
      } catch (error) {
        console.error("Error fetching org data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      fetchOrgData();
    } else {
      setLoading(false);
    }
  }, [organizationId, authData.accessToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const savedOrganizations = JSON.parse(
        localStorage.getItem("savedOrganizations") || "[]"
      );

      if (!editMode) {
        savedOrganizations.push(formData);
        localStorage.setItem(
          "savedOrganizations",
          JSON.stringify(savedOrganizations)
        );
      }

      const payload = { 
        ...formData, 
        active: formData.isActive,
        vastOnly: formData.vastOnly,
        organizationLogoFileName: organizationLogoFileName,
      };
      const method = editMode ? "PUT" : "POST";
      const url = editMode
        ? `${config.BASE_URL}/organizations/${formData.id}`
        : `${config.BASE_URL}/organizations`;

   
      const response = await fetch(url, {
        method: method,
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await response.json();


        const restrictedFields = ['couchbaseScope', 'syncGatewayDB', 'dbUserRole'];
        const newLockedFields = { ...lockedFields };
        const newFieldsUpdated = { ...fieldsUpdated };
        
        restrictedFields.forEach(fieldName => {
          const fieldValue = formData[fieldName]?.trim() || "";
          if (fieldValue !== "") {

            newLockedFields[fieldName] = true;
            newFieldsUpdated[fieldName] = true;
          }
        });
        
        setLockedFields(newLockedFields);
        setFieldsUpdated(newFieldsUpdated);

        setPopupMessage(
          editMode
            ? "Organization updated successfully!"
            : "Organization saved successfully!"
        );
        setPopupType("success");
        setShowPopup(true);

        setTimeout(() => {
          setShowPopup(false);
          navigate("/organizations");
        }, 4000);
      } else {
        console.error("Failed to save organization");
        setPopupMessage(
          editMode
            ? "Error updating organization!"
            : "Error saving organization!"
        );
        setPopupType("error");
        setShowPopup(true);
        setTimeout(() => {
          setShowPopup(false);
          setIsSubmitting(false);
        }, 4000);
      }
    } catch (error) {
      console.error("Error creating or updating organization:", error);
      setPopupMessage(
        editMode ? "Error updating organization!" : "Error saving organization!"
      );
      setPopupType("error");
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setIsSubmitting(false);
      }, 4000);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <div className="create-organization-page">
      {showPopup && <div className={`popup-${popupType}`}>{popupMessage}</div>}

      <div className="create-organization-content-wrapper">
        <div className="create-organization-header">
          <Button
            className="create-organization-back-button"
            onClick={handleBackClick}
            text="← Back"
            color="#006d7d"
          />
          <h1>{title}</h1>
        </div>
        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="create-organization-form-layout">
            <div className="create-organization-form-container">
              <div className="create-organization-form-inner">
                <div className="create-organization-form-left">
            <div className="create-organization-form-group">
              <label>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="create-organization-form-group">
              <label>City *</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
              />
            </div>
            <div className="create-organization-form-group">
              <label>State/Province/Region *</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
              />
            </div>
            <div className="create-organization-form-group">
              <label>Country *</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
              />
            </div>
            <div className="create-organization-form-group">
              <label>Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
              >
                <option value="">Select Type</option>
                <option value="NGO">NGO</option>
                <option value="HOSPITAL">Hospital</option>
                <option value="SHORT_TERM">Short Term</option>
              </select>
            </div>
            <div className="create-organization-form-group">
              <label>Joined Date *</label>
              <input
                type="date"
                name="joinedDate"
                value={formData.joinedDate || ""}
                onChange={handleChange}
                required
                {...(!editMode && { min: todayDate })}
              />
            </div>
            {/* <div className="create-organization-form-group">
              <label>Couchbase Scope</label>
              <input
                type="text"
                name="couchbaseScope"
                value={formData.couchbaseScope}
                onChange={handleChange}
                disabled={lockedFields.couchbaseScope}
                title={lockedFields.couchbaseScope ? "This field cannot be updated" : ""}
              />
            </div>
            <div className="create-organization-form-group">
              <label>Sync Gateway DB</label>
              <input
                type="text"
                name="syncGatewayDB"
                value={formData.syncGatewayDB}
                onChange={handleChange}
                disabled={lockedFields.syncGatewayDB}
                title={lockedFields.syncGatewayDB ? "This field cannot be updated" : ""}
              />
            </div>
            <div className="create-organization-form-group">
              <label>DB User Role</label>
              <input
                type="text"
                name="dbUserRole"
                value={formData.dbUserRole}
                onChange={handleChange}
                disabled={lockedFields.dbUserRole}
                title={lockedFields.dbUserRole ? "This field cannot be updated" : ""}
              />
            </div> */}
            <div className="create-organization-form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </div>
            <div className="create-organization-form-group is-active-wrapper">
              <label>Active</label>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
            </div>
            <div className="create-organization-form-group is-active-wrapper">
              <label>Vast Only</label>
              <input
                type="checkbox"
                name="vastOnly"
                checked={formData.vastOnly}
                onChange={handleChange}
              />
            </div>

                </div>

                <div
                  className="create-organization-form-right"
                  aria-label="Organization logo upload"
                >
                  <div className="create-organization-logo-title">
                    Organization Logo
                  </div>

                  <div className="create-organization-logo-upload-area">
                    {organizationLogoFileName ? (
                      <>
                        <button
                          type="button"
                          className="create-organization-delete-logo-btn"
                          onClick={handleOrganizationLogoDelete}
                          aria-label="Delete organization logo"
                        >
                          <Trash2 size={20} />
                        </button>
                        <label htmlFor="organization-logo-upload">
                          <img
                            src={organizationLogoUrl}
                            alt="Organization Logo"
                            className="create-organization-uploaded-logo"
                          />
                        </label>
                      </>
                    ) : (
                      <label htmlFor="organization-logo-upload">
                        <UserCircle size={110} />
                      </label>
                    )}

                    <input
                      type="file"
                      id="organization-logo-upload"
                      ref={logoInputRef}
                      accept=".png,.jpg,.jpeg"
                      onChange={(e) => {
                        handleOrganizationLogoUpload(e);
                        e.target.value = "";
                      }}
                      style={{ display: "none" }}
                      disabled={uploadingLogo}
                    />
                  </div>

                  <div className="create-organization-logo-help">
                    Accepted: .png, .jpg, .jpeg
                    <br />
                    Max size: 2 MB
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isInventoryAccessEnabled && (
            <div className="inventory-section">
              <div className="inventory-tab-header">
                <button
                  type="button"
                  className={`inventory-tab-button ${activeTab === "inventory" ? "active" : ""}`}
                  onClick={() => setActiveTab("inventory")}
                >
                  Inventory
                </button>
              </div>

              {activeTab === "inventory" && (
                <div className="inventory-form-container">
                  <div className="inventory-header">
                    <div className="org-toggle-container">
                      <label className="org-toggle-label">Using Inventory Management</label>
                      <label className="org-toggle-switch">
                        <input
                          type="checkbox"
                          name="usingInventoryManagement"
                          checked={formData.usingInventoryManagement}
                          onChange={handleChange}
                        />
                        <span className="org-toggle-slider"></span>
                      </label>
                    </div>
                  </div>

                  <OrganizationLocation
                    locations={formData.locationHierarchy}
                    onLocationsChange={handleLocationHierarchyChange}
                  />

                  <div className="inventory-categories-section">
                    <h4 className="inventory-categories-title">Category</h4>

                    <div className="inventory-categories-grid">
                      <div className="inventory-category-row">
                        <div className="inventory-category-row-label">
                          Medication Categories
                        </div>
                        <textarea
                          value={formData.inventoryCategories.medicationCategories}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              inventoryCategories: {
                                ...prev.inventoryCategories,
                                medicationCategories: e.target.value,
                              },
                            }))
                          }
                          placeholder="Enter list of medication categories..."
                          className="inventory-category-textarea"
                        />
                        <div className="inventory-restriction-checkbox">
                          <label>
                            <input
                              type="checkbox"
                              checked={
                                formData.inventoryCategories.medicationCategoriesRestriction
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  inventoryCategories: {
                                    ...prev.inventoryCategories,
                                    medicationCategoriesRestriction: e.target.checked,
                                  },
                                }))
                              }
                            />
                            <span style={{ marginLeft: "8px" }}>
                              Users must select from drop down list ONLY
                            </span>
                          </label>
                          <p className="inventory-restriction-info">
                            Enable if you want to restrict users from typing in their own
                            value that is not in the list of values within the drop down for
                            this field.
                          </p>
                        </div>
                      </div>

                      <div className="inventory-category-row">
                        <div className="inventory-category-row-label">
                          Equipment Categories
                        </div>
                        <textarea
                          value={formData.inventoryCategories.equipmentCategories}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              inventoryCategories: {
                                ...prev.inventoryCategories,
                                equipmentCategories: e.target.value,
                              },
                            }))
                          }
                          placeholder="Enter list of equipment categories..."
                          className="inventory-category-textarea"
                        />
                        <div className="inventory-restriction-checkbox">
                          <label>
                            <input
                              type="checkbox"
                              checked={
                                formData.inventoryCategories.equipmentCategoriesRestriction
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  inventoryCategories: {
                                    ...prev.inventoryCategories,
                                    equipmentCategoriesRestriction: e.target.checked,
                                  },
                                }))
                              }
                            />
                            <span style={{ marginLeft: "8px" }}>
                              Users must select from drop down list ONLY
                            </span>
                          </label>
                          <p className="inventory-restriction-info">
                            Enable if you want to restrict users from typing in their own
                            value that is not in the list of values within the drop down for
                            this field.
                          </p>
                        </div>
                      </div>

                      <div className="inventory-category-row">
                        <div className="inventory-category-row-label">
                          Supplies Categories
                        </div>
                        <textarea
                          value={formData.inventoryCategories.suppliesCategories}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              inventoryCategories: {
                                ...prev.inventoryCategories,
                                suppliesCategories: e.target.value,
                              },
                            }))
                          }
                          placeholder="Enter list of supplies categories..."
                          className="inventory-category-textarea"
                        />
                        <div className="inventory-restriction-checkbox">
                          <label>
                            <input
                              type="checkbox"
                              checked={
                                formData.inventoryCategories.suppliesCategoriesRestriction
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  inventoryCategories: {
                                    ...prev.inventoryCategories,
                                    suppliesCategoriesRestriction: e.target.checked,
                                  },
                                }))
                              }
                            />
                            <span style={{ marginLeft: "8px" }}>
                              Users must select from drop down list ONLY
                            </span>
                          </label>
                          <p className="inventory-restriction-info">
                            Enable if you want to restrict users from typing in their own
                            value that is not in the list of values within the drop down for
                            this field.
                          </p>
                        </div>
                      </div>

                      <div className="inventory-category-row">
                        <div className="inventory-category-row-label">
                          Food Categories
                        </div>
                        <textarea
                          value={formData.inventoryCategories.foodCategories}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              inventoryCategories: {
                                ...prev.inventoryCategories,
                                foodCategories: e.target.value,
                              },
                            }))
                          }
                          placeholder="Enter list of food categories..."
                          className="inventory-category-textarea"
                        />
                        <div className="inventory-restriction-checkbox">
                          <label>
                            <input
                              type="checkbox"
                              checked={formData.inventoryCategories.foodCategoriesRestriction}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  inventoryCategories: {
                                    ...prev.inventoryCategories,
                                    foodCategoriesRestriction: e.target.checked,
                                  },
                                }))
                              }
                            />
                            <span style={{ marginLeft: "8px" }}>
                              Users must select from drop down list ONLY
                            </span>
                          </label>
                          <p className="inventory-restriction-info">
                            Enable if you want to restrict users from typing in their own
                            value that is not in the list of values within the drop down for
                            this field.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="create-organization-save-button-container">
            <Button
              type="submit"
              className="create-organization-save-button"
              text={isSubmitting ? "Saving..." : "Save"}
              color="#006d7d"
              disabled={!isFormValid() || isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrganization;
